import type {TagColor} from "~/components/tag/tag.styles";
import type {RevenueShare, RevenueShareBand, SupplierExtLink} from "~/pages/supplier-360/types/Supplier360PdlTypes";

export const NO_VALUE = '—';

/** Whether `MatchReviewBanner` would render anything for this link — callers use this to decide whether to show their whole section even with no other data to display. */
export function hasReviewNotice(link: SupplierExtLink | undefined): boolean {
    return Boolean(link && (link.status === 'candidate' || link.review_reason));
}

/** FE-only display thresholds — not tied to any backend policy (there is no server-side workforce-signal engine yet). */
const GROWTH_BAD_PCT = -5;
const GROWTH_GOOD_PCT = 0;
export const CHURN_WARN_PCT = 25;

export function formatPct(value: number | null | undefined, {signed = false, digits = 1} = {}): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return NO_VALUE;
    const rounded = Number(value.toFixed(digits));
    const sign = signed && rounded > 0 ? '+' : '';
    return `${sign}${rounded.toLocaleString('en-GB')}%`;
}

export function formatCount(value: number | null | undefined): string {
    return value === null || value === undefined ? NO_VALUE : value.toLocaleString('en-GB');
}

export function growthColor(pct: number | null | undefined): TagColor {
    if (pct === null || pct === undefined) return 'default';
    if (pct <= GROWTH_BAD_PCT) return 'error';
    if (pct < GROWTH_GOOD_PCT) return 'warning';
    return 'success';
}

/** `$1.2B`, `$350M`, `$40K` — company revenue figures here are always USD. */
export function formatUsd(value: number | null | undefined): string {
    if (value === null || value === undefined) return NO_VALUE;
    const units: [number, string][] = [[1e12, 'T'], [1e9, 'B'], [1e6, 'M'], [1e3, 'K']];
    for (const [size, suffix] of units) {
        if (Math.abs(value) >= size) return `$${Number((value / size).toFixed(1)).toLocaleString('en-GB')}${suffix}`;
    }
    return `$${value.toLocaleString('en-GB')}`;
}

/** Shows a range only when the figure is an inferred band (`min !== max`) rather than a precise one. */
export function formatUsdRange(min: number | null | undefined, max: number | null | undefined): string {
    if (min === null || min === undefined) return NO_VALUE;
    if (max === null || max === undefined || max === min) return formatUsd(min);
    return `${formatUsd(min)}–${formatUsd(max)}`;
}

/** Matches `supplier_risk_intel.leverage.BANDS`' own thresholds (1% / 5% / 20%). */
export const BAND_LIMITS: {band: RevenueShareBand, upTo: number | null}[] = [
    {band: 'low_leverage', upTo: 1},
    {band: 'moderate_leverage', upTo: 5},
    {band: 'significant_leverage', upTo: 20},
    {band: 'critical_leverage', upTo: null},
];

export const BAND_LABEL: Record<RevenueShareBand, string> = {
    low_leverage: 'low leverage',
    moderate_leverage: 'moderate leverage',
    significant_leverage: 'significant leverage',
    critical_leverage: 'critical leverage',
};

export const BAND_COLOR: Record<RevenueShareBand, TagColor> = {
    low_leverage: 'default',
    moderate_leverage: 'info',
    significant_leverage: 'warning',
    critical_leverage: 'error',
};

const BAND_READ: Record<RevenueShareBand, string> = {
    low_leverage: 'low leverage; you are a small customer to them',
    moderate_leverage: 'moderate leverage; worth keeping an eye on',
    significant_leverage: 'significant leverage; you are an important customer',
    critical_leverage: 'critical leverage; the supplier is highly dependent on you',
};

export function formatShare(share: RevenueShare): string {
    return formatPct(share.pct, {digits: 2});
}

/** The leverage card's one-line read, e.g. "You are 3.1% of Biffa's revenue — moderate leverage; ...". */
export function leverageRead(share: RevenueShare, supplierName: string): string {
    return `You are ${formatShare(share)} of ${supplierName}'s revenue — ${BAND_READ[share.band]}.`;
}

/** ISO timestamp -> `Sep 2026`, for "Updated Sep 2026". */
export function updatedMonth(iso: string | null | undefined): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', {month: 'short', year: 'numeric'});
}

/** Title-cases a lowercase provider value ("high wycombe" -> "High Wycombe"). */
export function titleCase(text: string): string {
    return text.replace(/\b\p{L}/gu, c => c.toUpperCase());
}
